import { Button } from "@design";
import { useQuery } from "@tanstack/react-query";
import { FolderPlus, Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { listSpacesOptions } from "@/api";
import { Page } from "@/components/Page";
import { QuerySection } from "@/components/section";
import { Text } from "@/components/Text";
import { NewSpaceModal } from "./NewSpaceModal";
import { SpaceCard } from "./SpaceCard";
import { SpacesActivity } from "./SpacesActivity";
import { useSpaceStars } from "./useSpaceStars";

export function SpacesPage() {
  const { t } = useTranslation("spaces");
  const [creating, setCreating] = useState(false);
  const [archived, setArchived] = useState(false);
  const spaces = useQuery({
    ...listSpacesOptions({ query: { page_size: 100, ...(archived ? { status: "archived" } : {}) } }),
    select: (r) => r.data,
  });
  const title = archived ? t("list.archivedTitle") : t("all.title");
  const stars = useSpaceStars();

  return (
    <Page
      title={t("title")}
      subtitle={t("subtitle")}
      actions={
        <Button variant="primary" size="sm" leadingIcon={<Plus size={16} />} onClick={() => setCreating(true)}>
          {t("newSpace")}
        </Button>
      }
    >
      <SpacesActivity />
      <section aria-label={title} className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-2">
          <Text as="h2" variant="eyebrow" tone="tertiary">
            {title}
          </Text>
          {!archived && (
            <Text variant="caption" tone="tertiary">
              {t("all.hint")}
            </Text>
          )}
          <span className="ml-auto">
            <Button variant="text" size="sm" onClick={() => setArchived((a) => !a)}>
              {archived ? t("list.hideArchived") : t("list.showArchived")}
            </Button>
          </span>
        </div>
        <QuerySection
          query={spaces}
          isEmpty={(list) => list.length === 0}
          empty={{ icon: <FolderPlus size={18} />, hint: archived ? t("list.archivedEmpty") : t("all.empty") }}
          loadingRows={4}
        >
          {(list) => (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...list]
                .sort((a, b) => Number(b.isPersonal) - Number(a.isPersonal))
                .map((space) => (
                  <li key={space.uuid} className="flex">
                    <SpaceCard
                      space={space}
                      starred={stars.isStarred(space.uuid)}
                      onToggleStar={() => stars.toggle(space.uuid, stars.isStarred(space.uuid))}
                    />
                  </li>
                ))}
            </ul>
          )}
        </QuerySection>
      </section>
      {creating && <NewSpaceModal onClose={() => setCreating(false)} />}
    </Page>
  );
}
